import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { FormlyFieldConfig, FormlyFormOptions, FormlyModule } from '@ngx-formly/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from './auth.service';
import { EMPTY, catchError, filter, switchMap, tap } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'oc-join',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormlyBootstrapModule, FormlyModule],
  template: `
    <div class="fullscreen-container primary-background d-flex">
      <div class="background background-1"></div>
      <div class="pane d-flex flex-column justify-content-center">
        <div class="pane-padding">
          <h1>Join</h1>
          <form [formGroup]="form" (ngSubmit)="onSubmit(model)">
            <formly-form [form]="form" [fields]="fields" [model]="model" [options]="options"></formly-form>
            <button type="submit" class="btn btn-primary w-100 mt-3 mb-4">
              <span *ngIf="signingUp" class="spinner-border spinner-border-sm"></span>
              <span *ngIf="!signingUp">Sign Up</span>
            </button>
          </form>
        </div>
      </div>
    </div>
  `,
  styles: `
    h1 {
      margin-bottom: 1.75rem;
    }
  `,
})
export class JoinComponent {
  private auth = inject(AuthService);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private route = inject(ActivatedRoute);

  signingUp = false;
  form = new FormGroup({});
  model = { email: '', password: '' };

  code: string;

  options: FormlyFormOptions = {
    formState: {
      submitted: false,
    },
  };

  fields: FormlyFieldConfig[] = [
    {
      validators: {
        validation: [{ name: 'fieldMatch', options: { errorPath: 'passwordConfirm' } }],
      },
      fieldGroup: [
        {
          fieldGroupClassName: 'row',
          fieldGroup: [
            {
              className: 'col-6 col-tight',
              key: 'firstName',
              type: 'input',
              props: {
                label: 'FirstName',
                required: true,
                hideRequiredMarker: true,
              },
            },
            {
              className: 'col-6 col-tight',
              key: 'lastName',
              type: 'input',
              props: {
                label: 'Last Name',
                required: true,
                type: 'email',
                hideRequiredMarker: true,
              },
            },
          ],
        },

        {
          key: 'email',
          type: 'input',
          props: {
            label: 'Email',
            required: true,
            type: 'email',
            hideRequiredMarker: true,
          },
          validators: {
            validation: ['email'],
          },
        },
        {
          key: 'password',
          type: 'input',
          props: {
            label: 'Password',
            required: true,
            type: 'password',
            hideRequiredMarker: true,
          },
        },
        {
          key: 'passwordConfirm',
          type: 'input',
          props: {
            label: 'Confirm Password',
            required: true,
            type: 'password',
            hideRequiredMarker: true,
          },
        },
      ],
    },
  ];

  constructor() {
    this.code = this.route.snapshot.queryParams['code'];
  }

  onSubmit(model: any) {
    this.options.formState.submitted = true;

    if (this.signingUp) {
      return;
    }

    if (!this.form.valid) {
      this.toastr.error('Please review your login details and try again');
      return;
    }

    this.signingUp = true;

    const joinData = {
      ...model,
      code: this.code,
    };

    this.auth
      .join(joinData)
      .pipe(
        catchError((err) => {
          this.toastr.error(err.message);
          this.signingUp = false;
          return EMPTY;
        }),
        switchMap(() => {
          return this.auth.login(joinData.email, joinData.password);
        }),
        switchMap(() => {
          return this.auth.auth$.pipe(filter((auth) => !!auth));
        }),
        tap(() => {
          this.router.navigate(['/']);
        }),
      )
      .subscribe();
  }
}
